import React from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { servicesPath } from "../lib/paths";

const CONTENT = {
  spelling: {
    es: {
      title: "Ortografía y ortotipografía",
      intro: "Dejo tu texto libre de errores: tildes, puntuación, concordancias y ortotipografía editorial.",
      bullets: [
        "Ortografía (RAE/ASALE): tildes, mayúsculas, abreviaturas.",
        "Puntuación y sintaxis: comas, puntos, corrección de ambigüedades.",
        "Ortotipografía editorial: cursivas, comillas, rayas, números, siglas.",
        "Informe breve de incidencias y recomendaciones.",
      ],
      deliverable: "Entrega en Word con control de cambios y PDF limpio.",
    },
    de: {
      title: "Rechtschreibung & Orthotypografie",
      intro: "Fehlerfreie Texte: Rechtschreibung, Zeichensetzung und orthotypografische Normen nach Duden.",
      bullets: [
        "Rechtschreibung und Grammatik nach Duden.",
        "Zeichensetzung und Syntax.",
        "Orthotypografie: Kursiv, Anführungszeichen, Gedankenstriche, Zahlen, Abkürzungen.",
        "Kurzer Befundbericht mit Empfehlungen.",
      ],
      deliverable: "Lieferung als Word mit Änderungsverfolgung und sauberes PDF.",
    },
  },
  style: {
    es: {
      title: "Estilo y tono narrativo",
      intro: "Pulo tu voz para que resulte clara, natural y cautivadora, sin traicionar tu identidad.",
      bullets: [
        "Coherencia de voz y registro.",
        "Ritmo, cadencia y fluidez de lectura.",
        "Diálogos, descripciones y balance de escenas.",
        "Eliminación de redundancias y muletillas.",
      ],
      deliverable: "Versión editada con sugerencias + comentarios justificados.",
    },
    de: {
      title: "Stil & narrativer Ton",
      intro: "Ich schärfe Ihre Stimme für Klarheit, Natürlichkeit und Sogwirkung — ohne sie zu verfälschen.",
      bullets: [
        "Stimm- und Registerkohärenz.",
        "Rhythmus, Lesefluss und Spannung.",
        "Dialoge, Beschreibungen und Szenenbalance.",
        "Streichung von Redundanzen und Füllwörtern.",
      ],
      deliverable: "Bearbeitete Fassung mit Vorschlägen und begründeten Kommentaren.",
    },
  },
  layout: {
    es: {
      title: "Maquetación y diseño",
      intro: "Maquetación profesional para impresión y eBook: tipografías, jerarquías, márgenes y detalles editoriales.",
      bullets: [
        "Diseño interior para tapa blanda/dura.",
        "Selección tipográfica y estilos (títulos, subtítulos, capitulares).",
        "Paginación, folios, viudas y huérfanas, notas al pie.",
        "Exportación optimizada: PDF listo para imprenta y ePub/Kindle.",
      ],
      deliverable: "PDF final y EPUB/Kindle, con los archivos fuente si los necesitas.",
    },
    de: {
      title: "Satz & Gestaltung",
      intro: "Professioneller Buchsatz für Print und eBook: Typografie, Hierarchien, Ränder und alle editorischen Details.",
      bullets: [
        "Innengestaltung für Taschenbuch und Hardcover.",
        "Typo-Auswahl, Absatz-/Zeichenformate, Kapitälchen/Initialen.",
        "Paginierung, Schusterjungen/Hurenkinder, Fußnoten.",
        "Export: Druck-PDF und ePub/Kindle optimiert.",
      ],
      deliverable: "Fertiges PDF und EPUB/Kindle, auf Wunsch samt Quelldateien.",
    },
  },
  analysis: {
    es: {
      title: "Análisis literario",
      intro: "Una lectura profesional para detectar fortalezas y áreas de mejora. Te doy un mapa para llevar tu obra al siguiente nivel.",
      bullets: [
        "Informe editorial (6–10 págs.) con diagnóstico claro.",
        "Trama, personajes, ritmo, punto de vista y coherencia.",
        "Propuestas estructurales y de reescritura.",
        "Siguientes pasos recomendados y bibliografía de apoyo.",
      ],
      deliverable: "PDF del informe y, si quieres, media hora de conversación para comentarlo.",
    },
    de: {
      title: "Literarische Analyse",
      intro: "Professionelle Lektüre mit klarer Diagnose von Stärken und Potenzial. Ein Fahrplan für die nächste Stufe.",
      bullets: [
        "Lektoratsbericht (6–10 Seiten).",
        "Plot, Figuren, Rhythmus, Perspektive und Kohärenz.",
        "Struktur- und Überarbeitungsvorschläge.",
        "Nächste Schritte und optionale Leseempfehlungen.",
      ],
      deliverable: "Bericht als PDF + optional 30-minütiges Feedback-Gespräch.",
    },
  },
};

export default function ServiceDetail({ language }) {
  const { id } = useParams();
  const data = CONTENT[id]?.[language];

  if (!data) {
    return (
      <div className="min-h-[60vh] bg-papel text-tinta flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="font-serif text-2xl tracking-titulo mb-3">
            {language === "de" ? "Service nicht gefunden" : "Servicio no encontrado"}
          </h1>
          <Link to={`/${language}`} className="rotulo text-loboOscuro underline underline-offset-4">
            {language === "de" ? "Zurück zur Startseite" : "Volver al inicio"}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-papel text-tinta">
      <Helmet>
        <title>{`${data.title} – Blauer Wolf Verlag`}</title>
        <meta name="description" content={data.intro.slice(0, 158)} />
      </Helmet>

      <section className="mx-auto max-w-3xl px-5 sm:px-8 py-14 sm:py-20">
        <Link
          to={servicesPath(language)}
          className="rotulo text-sombra hover:text-tinta transition-colors"
        >
          {language === "de" ? "← Werkstatt" : "← Taller"}
        </Link>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-titulo mb-6 mt-8">
          {data.title}
        </h1>
        <p className="text-lg text-tinta/80 mb-12 max-w-prosa">{data.intro}</p>

        <div className="border-t border-tinta/20 pt-7 mb-10">
          <ul className="space-y-3 text-tinta/85 [&>li]:pl-5 [&>li]:relative [&>li:before]:content-['—'] [&>li:before]:absolute [&>li:before]:left-0 [&>li:before]:text-lobo">
            {data.bullets.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
        </div>

        <div className="border border-tinta/20 p-6">
          <p className="text-tinta/85">
            <strong className="rotulo text-loboOscuro mr-2">
              {language === "de" ? "Lieferung:" : "Entrega:"}
            </strong>{" "}
            {data.deliverable}
          </p>
        </div>

        <div className="mt-10 flex flex-wrap gap-4">
          <Link
            to={`/${language}#kontakt`}
            className="rotulo bg-tinta text-hueso px-7 py-3.5 hover:bg-loboOscuro transition-colors"
          >
            {language === "de" ? "Angebot anfordern" : "Solicitar presupuesto"}
          </Link>
          <Link
            to={servicesPath(language)}
            className="rotulo border border-tinta/25 px-7 py-3.5 hover:border-tinta transition-colors"
          >
            {language === "de" ? "Alle Leistungen" : "Todos los servicios"}
          </Link>
        </div>
      </section>
    </div>
  );
}
